import { useBrandTitle } from '@/hooks/useBranding';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { Layout } from '@/components/Layout';
import { Login } from '@/pages/Login';
import { Home } from '@/pages/Home';
import { Apps } from '@/pages/Apps';
import AppsSetup from '@/pages/AppsSetup';
import { UpdateBatches } from '@/pages/UpdateBatches';
import { AppFactory } from '@/pages/AppFactory';
import { UpdateBatchDetail } from '@/pages/UpdateBatchDetail';
import { Aso } from './pages/Aso';
import { Radar } from './pages/Radar';
import { Campaigns } from '@/pages/Campaigns';
import { Ads } from '@/pages/Ads';
import { Pwa } from '@/pages/Pwa';
import { Funnel } from '@/pages/Funnel';
import { Offers } from '@/pages/Offers';
import { OfferPerformance } from '@/pages/OfferPerformance';
import { Performance } from '@/pages/Performance';
import { Reconciliation } from '@/pages/Reconciliation';
import { Publishers } from '@/pages/Publishers';
import { Generate } from '@/pages/Generate';
import { Referrals } from '@/pages/Referrals';
import { Devices } from '@/pages/Devices';
import { Firebase } from '@/pages/Firebase';
import { FirebaseDetail } from '@/pages/FirebaseDetail';
import { Play } from '@/pages/Play';
import { PlayPublish } from '@/pages/PlayPublish';
import { LiveBrowser } from '@/pages/play/LiveBrowser';
import { BrowserProfiles } from '@/pages/BrowserProfiles';
import { PlayVitals } from '@/pages/PlayVitals';
import { PlayDetail } from '@/pages/PlayDetail';
import { Tracker } from '@/pages/Tracker';
import { TrackerDomains } from '@/pages/TrackerDomains';
import { SeoDomains } from '@/pages/SeoDomains';
import { TrackerCampaigns } from '@/pages/TrackerCampaigns';
import { TrackerGeoRouters } from '@/pages/TrackerGeoRouters';
import { TrackerPwas } from '@/pages/TrackerPwas';
import { TrackerClicks } from '@/pages/TrackerClicks';
import { TrackerConversions } from '@/pages/TrackerConversions';
import { PostbackLogs } from '@/pages/PostbackLogs';
import { ClientActivity } from '@/pages/ClientActivity';
import { ClientFactory } from '@/pages/ClientFactory';
import { Playables } from '@/pages/Playables';
import { PlayableLinks } from '@/pages/PlayableLinks';
import { PlayableStats } from '@/pages/PlayableStats';
import { PushApps } from '@/pages/PushApps';
import { PushTest } from '@/pages/PushTest';
import { PushTemplates } from '@/pages/PushTemplates';
import { PushSchedules } from '@/pages/PushSchedules';
import { PushScheduleDetail } from '@/pages/PushScheduleDetail';
import { PushAnalytics } from '@/pages/PushAnalytics';
import { Settings } from '@/pages/Settings';
import { Qa } from '@/pages/Qa';
import { Notifications } from '@/pages/Notifications';
import { Users } from '@/pages/Users';
import { PublicApi } from '@/pages/PublicApi';
import { AuditLog } from '@/pages/AuditLog';
import { Checklist } from '@/pages/Checklist';
import { Credentials } from '@/pages/Credentials';
import { Facebook } from '@/pages/Facebook';
import Email from '@/pages/Email';
import { ChecklistPublic } from '@/pages/ChecklistPublic';
import { Wiki } from '@/pages/Wiki';
import { Deletions } from '@/pages/Deletions';
import { ClientApps } from '@/pages/ClientApps';
import { ClientOffers } from '@/pages/ClientOffers';
import { Account } from '@/pages/Account';
import { Toast } from '@/components/Toast';
import { WikiPublic } from '@/pages/WikiPublic';
import { AppHandoff } from '@/pages/AppHandoff';
import { WebviewTest } from '@/pages/WebviewTest';
import { FunnelAudits } from '@/pages/FunnelAudits';
import { FunnelAuditForm } from '@/pages/FunnelAuditForm';
import { FunnelAuditDetail } from '@/pages/FunnelAuditDetail';
import type { AdminSection } from '@/api/types';
import { markSessionActive, noteMissingToken } from '@/lib/logout-reason';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { token } = useAuthStore();
  if (!token) {
    noteMissingToken();
    return <Navigate to="/login" replace />;
  }
  markSessionActive();
  return <>{children}</>;
};

function SectionRoute({ section, children }: { section: AdminSection; children: React.ReactNode }) {
  const hasSection = useAuthStore((s) => s.hasSection);
  const admin = useAuthStore((s) => s.admin);
  if (!admin) return <Navigate to="/login" replace />;
  if (!hasSection(section)) return <NoAccess />;
  return <>{children}</>;
}

// Tracker Overview is aggregate performance reporting, which QA accounts don't
// get (the API 403s the underlying endpoint). Send them to Campaigns instead of
// a dead end — every tracker holder can reach it.
function TrackerOverviewRoute({ children }: { children: React.ReactNode }) {
  const isQa = useAuthStore((s) => s.isQa());
  if (isQa) return <Navigate to="/tracker/campaigns" replace />;
  return <>{children}</>;
}

// Pages under a section a client holds that are still ours: the factory,
// update batches, ASO intelligence, the requests queue. The API refuses them
// anyway; this keeps a typed URL from landing on a page of errors.
function NotForClientRoute({ children }: { children: React.ReactNode }) {
  const isClient = useAuthStore((s) => s.isClient());
  if (isClient) return <NoAccess />;
  return <>{children}</>;
}

// A client's offers are part of its tracker (the API maps them there too).
function OffersRoute() {
  const isClient = useAuthStore((s) => s.isClient());
  if (isClient) return <SectionRoute section="tracker"><ClientOffers /></SectionRoute>;
  return <SectionRoute section="offers"><Offers /></SectionRoute>;
}

// A page a client reaches under its own guard (its tracker), and staff under theirs.
function ClientOrStaff({ client, children }: { client: React.ReactNode; children: React.ReactNode }) {
  const isClient = useAuthStore((s) => s.isClient());
  return <>{isClient ? client : children}</>;
}

function AppsRoute() {
  const isClient = useAuthStore((s) => s.isClient());
  return isClient ? <ClientApps /> : <Apps />;
}

function ClientOnlyRoute({ children }: { children: React.ReactNode }) {
  const isClient = useAuthStore((s) => s.isClient());
  if (!isClient) return <NoAccess />;
  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const admin = useAuthStore((s) => s.admin);
  if (!admin) return <Navigate to="/login" replace />;
  if (admin.role !== 'super_admin') return <NoAccess />;
  return <>{children}</>;
}

function NoAccess() {
  return (
    <div className="max-w-md mx-auto mt-16 bg-surface border border-border rounded-lg p-6 text-center">
      <h2 className="text-lg font-semibold text-content">No access</h2>
      <p className="mt-2 text-sm text-content-muted">Your account doesn't have permission to view this section.</p>
    </div>
  );
}

// Section → its landing path, in sidebar order. Used to send a user to their
// first accessible section when they lack the `dashboard` section (developer,
// traffic, bdm) instead of dumping them on the "No access" card.
const SECTION_HOME: { section: AdminSection; path: string }[] = [
  { section: 'apps', path: '/apps' },
  { section: 'apps-setup', path: '/apps-setup' },
  { section: 'campaigns', path: '/campaigns' },
  { section: 'ads', path: '/ads' },
  { section: 'pwa', path: '/pwa' },
  { section: 'funnel', path: '/funnel' },
  { section: 'generate', path: '/generate' },
  { section: 'referrals', path: '/referrals' },
  { section: 'devices', path: '/devices' },
  { section: 'offers', path: '/offers' },
  { section: 'offers-performance', path: '/offers/performance' },
  { section: 'funnel-audits', path: '/funnel-audits' },
  { section: 'perf', path: '/performance' },
  { section: 'radar', path: '/radar' },
  { section: 'tracker', path: '/tracker' },
  { section: 'domains', path: '/tracker/domains' },
  { section: 'firebase', path: '/firebase' },
  { section: 'play', path: '/play' },
  { section: 'push', path: '/push/apps' },
  { section: 'push-test', path: '/push-test' },
  { section: 'notifications', path: '/notifications' },
  { section: 'audit', path: '/audit' },
  { section: 'checklist', path: '/checklist' },
  { section: 'credentials', path: '/credentials' },
  { section: 'facebook', path: '/facebook' },
  { section: 'email', path: '/email' },
  { section: 'qa', path: '/qa' },
  { section: 'settings', path: '/settings' },
];

function HomeRoute() {
  const admin = useAuthStore((s) => s.admin);
  const hasSection = useAuthStore((s) => s.hasSection);
  if (!admin) return <Navigate to="/login" replace />;
  if (hasSection('dashboard')) return <Home />;
  const first = SECTION_HOME.find((e) => hasSection(e.section));
  return first ? <Navigate to={first.path} replace /> : <NoAccess />;
}

function App() {
  useBrandTitle();
  return (
    <>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/checklist/public" element={<ChecklistPublic />} />
      {/* Share-token wiki view — deliberately outside ProtectedRoute. */}
      <Route path="/wiki/s/:token" element={<WikiPublic />} />
      <Route path="/checklist/webview-test" element={<WebviewTest />} />
      {/* Handing an app to an outside developer — no account, token only. */}
      <Route path="/handoff/:token" element={<AppHandoff />} />
      <Route path="/funnel-audits/new/:token" element={<div className="min-h-screen bg-surface-muted px-4 py-8"><FunnelAuditForm /></div>} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomeRoute />} />
        <Route path="apps" element={<SectionRoute section="apps"><AppsRoute /></SectionRoute>} />
        <Route path="apps-setup" element={<SectionRoute section="apps-setup"><AppsSetup /></SectionRoute>} />
        <Route path="apps-setup/factory" element={<SectionRoute section="apps-setup"><ClientOrStaff client={<ClientFactory />}><AppFactory /></ClientOrStaff></SectionRoute>} />
        <Route path="apps-setup/update-batches" element={<SectionRoute section="apps-setup"><NotForClientRoute><UpdateBatches /></NotForClientRoute></SectionRoute>} />
        <Route path="apps-setup/update-batches/:id" element={<SectionRoute section="apps-setup"><NotForClientRoute><UpdateBatchDetail /></NotForClientRoute></SectionRoute>} />
        <Route path="apps-setup/aso" element={<SectionRoute section="apps-setup"><NotForClientRoute><Aso /></NotForClientRoute></SectionRoute>} />
        <Route path="campaigns" element={<SectionRoute section="campaigns"><Campaigns /></SectionRoute>} />
        <Route path="ads" element={<SectionRoute section="ads"><Ads /></SectionRoute>} />
        <Route path="pwa" element={<SectionRoute section="pwa"><Pwa /></SectionRoute>} />
        <Route path="funnel" element={<SectionRoute section="funnel"><Funnel /></SectionRoute>} />
        <Route path="offers" element={<OffersRoute />} />
        <Route path="offers/performance" element={<ClientOrStaff client={<SectionRoute section="tracker"><OfferPerformance /></SectionRoute>}><SectionRoute section="offers-performance"><OfferPerformance /></SectionRoute></ClientOrStaff>} />
        <Route path="funnel-audits" element={<SectionRoute section="funnel-audits"><FunnelAudits /></SectionRoute>} />
        <Route path="funnel-audits/new" element={<SectionRoute section="funnel-audits"><FunnelAuditForm /></SectionRoute>} />
        <Route path="funnel-audits/:id" element={<SectionRoute section="funnel-audits"><FunnelAuditDetail /></SectionRoute>} />
        <Route path="funnel-audits/:id/edit" element={<SectionRoute section="funnel-audits"><FunnelAuditForm /></SectionRoute>} />
        <Route path="performance" element={<SectionRoute section="perf"><Performance /></SectionRoute>} />
        <Route path="radar" element={<SectionRoute section="radar"><Radar /></SectionRoute>} />
        <Route path="public-api" element={<SectionRoute section="public-api"><PublicApi /></SectionRoute>} />
        <Route path="finance/reconciliation" element={<SectionRoute section="finance"><Reconciliation /></SectionRoute>} />
        <Route path="finance/publishers" element={<SectionRoute section="finance"><Publishers /></SectionRoute>} />
        <Route path="generate" element={<SectionRoute section="generate"><Generate /></SectionRoute>} />
        <Route path="referrals" element={<SectionRoute section="referrals"><Referrals /></SectionRoute>} />
        <Route path="devices" element={<SectionRoute section="devices"><Devices /></SectionRoute>} />
        <Route path="firebase" element={<SectionRoute section="firebase"><Firebase /></SectionRoute>} />
        <Route path="firebase/:id" element={<SectionRoute section="firebase"><FirebaseDetail /></SectionRoute>} />
        <Route path="play" element={<SectionRoute section="play"><Play /></SectionRoute>} />
        <Route path="play/publish" element={<SectionRoute section="play"><PlayPublish /></SectionRoute>} />
        <Route path="play/live/:sessionId" element={<SectionRoute section="browser"><LiveBrowser /></SectionRoute>} />
        <Route path="browser-profiles" element={<SectionRoute section="browser"><BrowserProfiles /></SectionRoute>} />
        {/* Must precede play/:accountId — otherwise "vitals" is read as an account id. */}
        <Route path="play/vitals" element={<SectionRoute section="play"><PlayVitals /></SectionRoute>} />
        <Route path="play/:accountId" element={<SectionRoute section="play"><PlayDetail /></SectionRoute>} />
        <Route path="tracker" element={<SectionRoute section="tracker"><TrackerOverviewRoute><Tracker /></TrackerOverviewRoute></SectionRoute>} />
        <Route path="tracker/domains" element={<SectionRoute section="domains"><TrackerDomains /></SectionRoute>} />
        <Route path="tracker/seo-domains" element={<SuperAdminRoute><SeoDomains /></SuperAdminRoute>} />
        <Route path="tracker/campaigns" element={<SectionRoute section="tracker"><TrackerCampaigns /></SectionRoute>} />
        <Route path="tracker/geo-routers" element={<SectionRoute section="tracker"><TrackerGeoRouters /></SectionRoute>} />
        <Route path="tracker/clicks" element={<SectionRoute section="tracker"><TrackerClicks /></SectionRoute>} />
        <Route path="tracker/conversions" element={<SectionRoute section="tracker"><TrackerConversions /></SectionRoute>} />
        <Route path="tracker/postback-logs" element={<ClientOrStaff client={<SectionRoute section="tracker"><PostbackLogs /></SectionRoute>}><SuperAdminRoute><PostbackLogs /></SuperAdminRoute></ClientOrStaff>} />
        <Route path="tracker/pwas" element={<SectionRoute section="tracker"><TrackerPwas /></SectionRoute>} />
        <Route path="tracker/playables" element={<SectionRoute section="tracker"><Playables /></SectionRoute>} />
        <Route path="tracker/playable-links" element={<SectionRoute section="tracker"><PlayableLinks /></SectionRoute>} />
        <Route path="tracker/playable-stats" element={<SectionRoute section="tracker"><PlayableStats /></SectionRoute>} />
        <Route path="push/apps" element={<SectionRoute section="push"><PushApps /></SectionRoute>} />
        <Route path="push/templates" element={<SectionRoute section="push"><PushTemplates /></SectionRoute>} />
        <Route path="push/schedules" element={<SectionRoute section="push"><PushSchedules /></SectionRoute>} />
        <Route path="push/schedules/:id" element={<SectionRoute section="push"><PushScheduleDetail /></SectionRoute>} />
        <Route path="push/analytics" element={<SectionRoute section="push"><PushAnalytics /></SectionRoute>} />
        <Route path="push-test" element={<SectionRoute section="push-test"><PushTest /></SectionRoute>} />
        <Route path="settings" element={<SectionRoute section="settings"><Settings /></SectionRoute>} />
        <Route path="qa" element={<SectionRoute section="qa"><Qa /></SectionRoute>} />
        <Route path="notifications" element={<SectionRoute section="notifications"><Notifications /></SectionRoute>} />
        <Route path="users" element={<SectionRoute section="users"><Users /></SectionRoute>} />
        <Route path="audit" element={<SectionRoute section="audit"><AuditLog /></SectionRoute>} />
        <Route path="checklist" element={<SectionRoute section="checklist"><Checklist /></SectionRoute>} />
        <Route path="wiki" element={<SectionRoute section="wiki"><Wiki /></SectionRoute>} />
        <Route path="credentials" element={<SectionRoute section="credentials"><Credentials /></SectionRoute>} />
        <Route path="facebook" element={<SectionRoute section="facebook"><Facebook /></SectionRoute>} />
        <Route path="email" element={<SectionRoute section="email"><Email /></SectionRoute>} />
        {/* Deliberately not wrapped in SectionRoute: every role may see its own
            requests, and the API scopes the rows it returns. */}
        <Route path="deletions" element={<NotForClientRoute><Deletions /></NotForClientRoute>} />
        {/* Own password, and the team for a client company's primary login. */}
        <Route path="account" element={<Account />} />
        <Route path="activity" element={<ClientOnlyRoute><ClientActivity /></ClientOnlyRoute>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <Toast />
    </>
  );
}

export default App;
